import { styled } from '@porsche-code/kado-styled-system/jsx';
import { DateFormatSelect, Spacer } from '@porsche-code/kado-ui';
import {
  CheckboxChangeEventDetail,
  PCheckbox,
  PCheckboxProps,
  PHeading,
  PSelectProps,
  PText,
  SelectChangeEventDetail,
} from '@porsche-design-system/components-react';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { DateTimeOutput } from '@/components';
import { COMMON_NAMESPACE, NAMESPACES } from '@/config/i18n';
import { PersonActionKind, usePersonContext } from '@/context';
import {
  MeQuery,
  useMeQuery,
  useUpdatePersonSettingsMutation,
} from '@/graphql';

export const Route = createFileRoute('/profile/general-settings')({
  component: GeneralSettings,
  beforeLoad: () => {
    return {
      breadcrumb: () => (
        <Trans i18nKey="profile.generalSettings" ns={COMMON_NAMESPACE} />
      ),
    };
  },
});

function GeneralSettings() {
  const { t } = useTranslation(NAMESPACES);
  const queryClient = useQueryClient();
  const { dispatch: dispatchPerson, state: personState } = usePersonContext();
  const [currentDate] = useState(() => new Date());

  const { mutate: updatePersonSettings } = useUpdatePersonSettingsMutation({
    onSuccess: (data) => {
      const cacheKey = useMeQuery.getKey({ isAuthenticated: true } as never);
      const cache = queryClient.getQueryData<MeQuery>(cacheKey);

      if (cache?.me && cache.me.__typename === 'Person') {
        queryClient.setQueryData<MeQuery>(cacheKey, {
          me: {
            ...cache.me,
            settings: {
              ...cache.me?.settings,
              ...data.updatePersonSettings.settings,
            },
          },
        });
      }
    },
  });

  const change24hFormat = (event: CustomEvent<CheckboxChangeEventDetail>) => {
    const { checked } = event.target as HTMLElement & PCheckboxProps;

    updatePersonSettings({
      input: {
        is24HourFormat: !!checked,
      },
    });

    dispatchPerson({
      type: PersonActionKind.Change24HFormat,
      payload: !!checked,
    });
  };

  const changeDateFormat = (event: CustomEvent<SelectChangeEventDetail>) => {
    const { value } = event.target as HTMLElement & PSelectProps;

    updatePersonSettings({
      input: {
        dateFormat: value,
      },
    });

    dispatchPerson({
      type: PersonActionKind.ChangeDateFormat,
      payload: value,
    });
  };

  return (
    <>
      <PHeading aria-level={2} tag="h2" size="sm">
        {t('common:profile.dateSettings')}
      </PHeading>
      <Spacer mb="md" />
      <LimitWidth>
        <DateFormatSelect
          label={t('common:profile.dateFormat')}
          defaultValue={personState.settings?.dateFormat ?? undefined}
          onChange={changeDateFormat}
        />
      </LimitWidth>
      <Spacer mb="md" />
      <PCheckbox
        label={t('common:profile.24hFormat')}
        aria-label={t('common:profile.24hFormat')}
        name="is24hFormat"
        checked={personState.settings?.is24HourFormat ?? false}
        onChange={change24hFormat}
      />
      <Spacer mb="md" />
      <PText color="contrast-medium">
        {t('common:profile.dateExample')}:{' '}
        {currentDate && <DateTimeOutput date={currentDate} />}
      </PText>
    </>
  );
}

const LimitWidth = styled('div', {
  base: {
    maxWidth: '320px',
  },
});
